import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
// src/blocks/ErrorState/ErrorState.tsx
const errorStateClassnames = cva([
	"tw:flex",
	"tw:flex-col",
	"tw:items-center",
	"tw:text-center"
], {
	variants: { variant: {
		default: [
			"tw:py-16",
			"tw:px-8",
			"tw:gap-4"
		],
		compact: [
			"tw:py-8",
			"tw:px-4",
			"tw:gap-2"
		],
		destructive: [
			"tw:py-16",
			"tw:px-8",
			"tw:gap-4"
		]
	} },
	defaultVariants: { variant: "default" }
});
const errorStateIconClassnames = cva([
	"tw:flex",
	"tw:items-center",
	"tw:justify-center"
], {
	variants: { variant: {
		default: [
			"tw:size-16",
			"tw:text-4xl",
			"tw:[&_svg:not([class*=\"tw:size-\"])]:size-10",
			"tw:text-warning"
		],
		compact: [
			"tw:size-10",
			"tw:text-2xl",
			"tw:[&_svg:not([class*=\"tw:size-\"])]:size-6",
			"tw:text-warning"
		],
		destructive: [
			"tw:size-16",
			"tw:text-4xl",
			"tw:[&_svg:not([class*=\"tw:size-\"])]:size-10",
			"tw:text-destructive"
		]
	} },
	defaultVariants: { variant: "default" }
});
const errorStateTitleClassnames = cva(["tw:m-0", "tw:font-semibold"], {
	variants: { variant: {
		default: [
			"tw:text-lg",
			"tw:leading-7",
			"tw:text-foreground"
		],
		compact: [
			"tw:text-base",
			"tw:leading-6",
			"tw:text-foreground"
		],
		destructive: [
			"tw:text-lg",
			"tw:leading-7",
			"tw:text-destructive"
		]
	} },
	defaultVariants: { variant: "default" }
});
const errorStateDescriptionClassnames = cva(["tw:m-0", "tw:text-muted-foreground"], {
	variants: { variant: {
		default: [
			"tw:text-sm",
			"tw:leading-5",
			"tw:max-w-sm"
		],
		compact: [
			"tw:text-xs",
			"tw:leading-4",
			"tw:max-w-xs"
		],
		destructive: [
			"tw:text-sm",
			"tw:leading-5",
			"tw:max-w-sm"
		]
	} },
	defaultVariants: { variant: "default" }
});
const errorStateActionsClassnames = cva([
	"tw:flex",
	"tw:items-center",
	"tw:flex-wrap"
], {
	variants: { variant: {
		default: ["tw:gap-3", "tw:mt-2"],
		compact: ["tw:gap-2", "tw:mt-1"],
		destructive: ["tw:gap-3", "tw:mt-2"]
	} },
	defaultVariants: { variant: "default" }
});
/**
* Page-level error placeholder with `Icon`, `Title`, `Description` and `Actions` slots.
*
* - Use when the page can't render: failed request, no permission, missing entity.
* - Put a recovery action (retry, go back, request access) in `ErrorState.Actions`.
* - Not for transient failures (`toast()`), form validation (`FormGroup`) or banners (`Alert`).
*/
const ErrorStateRoot = ({ className, variant = "default", children, ...rest }) => /* @__PURE__ */ jsx("div", {
	role: "alert",
	className: errorStateClassnames({
		className,
		variant
	}),
	...rest,
	children
});
const ErrorStateIcon = ({ className, variant = "default", children, ...rest }) => /* @__PURE__ */ jsx("div", {
	className: errorStateIconClassnames({
		className,
		variant
	}),
	...rest,
	children
});
const ErrorStateTitle = ({ className, variant = "default", as: Tag = "h3", children, ...rest }) => /* @__PURE__ */ jsx(Tag, {
	className: errorStateTitleClassnames({
		className,
		variant
	}),
	...rest,
	children
});
const ErrorStateDescription = ({ className, variant = "default", children, ...rest }) => /* @__PURE__ */ jsx("p", {
	className: errorStateDescriptionClassnames({
		className,
		variant
	}),
	...rest,
	children
});
const ErrorStateActions = ({ className, variant = "default", children, ...rest }) => /* @__PURE__ */ jsx("div", {
	className: errorStateActionsClassnames({
		className,
		variant
	}),
	...rest,
	children
});
const ErrorState = ErrorStateRoot;
ErrorState.Icon = ErrorStateIcon;
ErrorState.Title = ErrorStateTitle;
ErrorState.Description = ErrorStateDescription;
ErrorState.Actions = ErrorStateActions;
export { ErrorState };

//# sourceMappingURL=ErrorState.js.map